// Pre-launch access mode.
//
// - Signed-out public viewers see the temporary launch site only: landing page,
//   waitlist, therapist directory, therapist application, about/support/legal/contact.
// - Signed-in members ALSO stay locked into the pre-launch experience until
//   the launch date (1 August 2026). Only admins bypass so they can QA the
//   in-progress member app.
//
// `THERAPIST_ONLY_MODE` is the SSR / fallback default and stays `true` so
// crawlers and first paint never expose the in-progress member app.

import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export const THERAPIST_ONLY_MODE = true;

// Full platform opens to members at this instant. Until then, only admins
// can browse the members-only app.
const LAUNCH_AT_UTC = Date.parse("2026-08-01T00:00:00.000Z");

function isLaunched(): boolean {
  return Date.now() >= LAUNCH_AT_UTC;
}

/**
 * Synchronous "are we still pre-launch?" check for non-hook call sites
 * (post-login routing, the /youre-in holding page). Honours the same
 * `rt_launch_override` test switch as `useTherapistOnlyMode` so QA can
 * simulate launch day without touching the clock or the database.
 */
export function isPreLaunchNow(): boolean {
  if (typeof window !== "undefined") {
    try {
      const v = window.localStorage.getItem("rt_launch_override");
      if (v === "live") return false;
      if (v === "prelaunch") return true;
    } catch {
      /* localStorage may be blocked */
    }
  }
  return !isLaunched();
}


async function userHasFullAppAccess(userId: string | null): Promise<boolean> {
  if (!userId) return false;
  if (isLaunched()) return true;
  // Pre-launch: only admins get the full app.
  try {
    const { data, error } = await supabase.rpc("has_role", {
      _user_id: userId,
      _role: "admin",
    });
    return !error && !!data;
  } catch {
    return false;
  }
}

/**
 * Returns `true` when the visitor should be locked into the pre-launch
 * therapist-only experience. Returns `false` once we've confirmed the
 * current user is an admin or approved tester.
 *
 * Defaults to `true` on first render so SSR / first paint never exposes
 * full-app navigation to signed-out public visitors.
 */
export function useTherapistOnlyMode(): boolean {
  const [therapistOnly, setTherapistOnly] = useState<boolean>(THERAPIST_ONLY_MODE);

  // Test-only override: lets Playwright simulate the post-launch state
  // ("live") or force pre-launch ("prelaunch") without touching the DB.
  // Read inside useEffect so SSR is unaffected.
  function readOverride(): boolean | null {
    if (typeof window === "undefined") return null;
    try {
      const v = window.localStorage.getItem("rt_launch_override");
      if (v === "live") return false;
      if (v === "prelaunch") return true;
    } catch {
      /* localStorage may be blocked */
    }
    return null;
  }

  useEffect(() => {
    let cancelled = false;

    async function refresh(userId: string | null) {
      const override = readOverride();
      if (override !== null) {
        if (!cancelled) setTherapistOnly(override);
        return;
      }
      const fullAccess = await userHasFullAppAccess(userId);
      if (!cancelled) setTherapistOnly(!fullAccess);
    }

    // Apply override on mount even before auth resolves.
    const initialOverride = readOverride();
    if (initialOverride !== null) setTherapistOnly(initialOverride);

    supabase.auth.getUser().then(({ data }) => {
      refresh(data.user?.id ?? null);
    });

    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as
        | { session?: { user?: { id?: string } } | null }
        | undefined;
      refresh(detail?.session?.user?.id ?? null);
    };
    window.addEventListener("rt-auth-change", handler);

    // Re-read override when tests flip it via a storage event.
    const storageHandler = (e: StorageEvent) => {
      if (e.key === "rt_launch_override") refresh(null);
    };
    window.addEventListener("storage", storageHandler);

    return () => {
      cancelled = true;
      window.removeEventListener("rt-auth-change", handler);
      window.removeEventListener("storage", storageHandler);
    };
  }, []);

  return therapistOnly;
}
